import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { useSignupViewModel } from "@/viewModels/useSignupViewModel";
import { AuthLayout } from "@/ui/layouts";
import {
  verificationSchema,
  type VerificationFormData,
} from "@/schemas/signupSchemas";

/**
 * VerifyPage Component
 *
 * Pure view component for email verification.
 * All business logic is in useSignupViewModel.
 *
 * Features:
 * - 6-digit verification code input
 * - Auto-focus on code input
 * - Resend code with 60-second cooldown
 * - Navigate to /login on successful verification
 */
export default function VerifyPage() {
  const { state, actions, computed } = useSignupViewModel();
  const { t } = useTranslation("auth");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationSchema),
    mode: "onChange",
  });

  // Note: Don't reset state on mount - we need the email from signup flow
  // State persists from SignupPage via Redux store

  // Form submission handler
  const onSubmit = handleSubmit((data) => {
    actions.verify(data.code);
  });

  // Resend code handler
  const handleResendCode = () => {
    actions.resendCode();
  };

  return (
    <AuthLayout>
      
        <div className="bg-white box-border flex flex-col gap-4 items-center px-4 py-6 rounded-[0.3125rem]">
          {/* Title */}
          <h2 className="font-montserrat font-semibold text-gray-900 text-xl leading-7 w-full text-center">
            {t("signup.verification.title")}
          </h2>

          {/* Subtitle with email */}
          <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 tracking-[0.3px] leading-5 w-full text-center">
            {t("signup.verification.subtitle", { email: state.email || "" })}
          </p>

          {/* Global Error Banner */}
          {computed.hasError && (
            <div
              role="alert"
              className="w-full bg-red-100 border border-red-200 text-brand-red rounded-[0.3125rem] px-4 py-3 text-sm"
            >
              {computed.errorMessage}
            </div>
          )}

          {/* Verification Form */}
          <form onSubmit={onSubmit} className="flex flex-col gap-4 w-full">
            {/* Code Input */}
            <div className="flex flex-col gap-1 w-full">
              <Input
                type="text"
                variant="anyw3ar"
                placeholder={t("signup.verification.code")}
                {...register("code")}
                aria-label={t("signup.verification.code")}
                aria-invalid={errors.code ? "true" : "false"}
                disabled={state.isVerifying}
                autoFocus
                maxLength={6}
                className={cn(
                  "text-center text-xl tracking-widest",
                  errors.code && "border-brand-red focus:ring-brand-red"
                )}
              />
              {errors.code && (
                <span className="text-brand-red text-sm" role="alert">
                  {t(errors.code.message || "")}
                </span>
              )}
            </div>

            {/* Verify Button */}
            <Button
              type="submit"
              variant="primary-red"
              size="primary-red"
              className="w-full"
              disabled={state.isVerifying}
            >
              {state.isVerifying
                ? t("loading.verifying")
                : t("signup.verification.submit")}
            </Button>
          </form>

          {/* Divider Line */}
          <div className="bg-gray-100 h-[0.03125rem] w-full" />

          {/* Resend Code Button */}
          <div className="flex flex-col gap-2 items-center w-full">
            {state.resendCooldown > 0 ? (
              <p className="font-montserrat font-normal text-sm text-gray-600 tracking-[0.3px] leading-5">
                {t("signup.verification.resendCooldown", {
                  seconds: state.resendCooldown,
                })}
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="font-montserrat font-normal text-sm text-brand-red hover:underline focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!computed.canResend}
              >
                {t("signup.verification.resend")}
              </button>
            )}
          </div>
        </div>
      </AuthLayout>
  );
}
